import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  VStack,
  HStack,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  Icon,
  Container,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  useColorModeValue,
  useBreakpointValue,
  
  Center,
  Tooltip
} from '@chakra-ui/react';
import { FiTrash2, FiPlus, FiVideo, FiUpload, FiPlayCircle, FiUsers, FiMonitor, FiStar } from 'react-icons/fi';
import { motion } from 'framer-motion';
import axiosInstance from '../services/axiosInstance';
import { useSidebar } from '../utils/SidebarContext';
import SidebarAdmin from '../shared/Sidebar2';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const StatCard = ({ title, stat, icon, color }) => {
  return (
    <Card bg="white" shadow="sm" borderRadius="xl" borderTop="4px solid" borderColor={`${color}.400`} _hover={{ shadow: 'md', transform: 'translateY(-2px)' }} transition="all 0.2s">
      <CardBody p={5}>
        <Flex justify="space-between" align="center">
          <Stat>
            <StatLabel fontSize="sm" color="gray.500" fontWeight="700" mb={1}>{title}</StatLabel>
            <StatNumber fontSize="3xl" fontWeight="900" color="gray.700">{stat}</StatNumber>
          </Stat>
          <Flex bg={`${color}.50`} p={4} borderRadius="xl" align="center" justify="center">
            <Icon as={icon} boxSize={7} color={`${color}.500`} />
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};

const AdminFeedbackVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const fileInputRef = useRef();
  
  const { isOpen: isSidebarOpen } = useSidebar();
  const bgColor = useColorModeValue('#f4f7fe', 'gray.900'); // Professional soft blue-gray background
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isSidebarOpen ? '250px' : '100px' });

  const [formData, setFormData] = useState({
    title: '',
    type: 'parent',
    file: null
  });

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.get('/feedback-videos/admin');
      setVideos(data);
    } catch (error) {
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء جلب الفيديوهات',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.file || !formData.type) {
      toast({
        title: 'تنبيه',
        description: 'يرجى ملء جميع الحقول المطلوبة',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setUploading(true);
      const data = new FormData();
      data.append('title', formData.title);
      data.append('type', formData.type);
      data.append('file', formData.file);

      await axiosInstance.post('/feedback-videos', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast({
        title: 'عملية ناجحة',
        description: 'تم رفع وإضافة الفيديو بنجاح',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setFormData({ title: '', type: 'parent', file: null });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onClose();
      fetchVideos();
    } catch (error) {
      toast({
        title: 'خطأ',
        description: error.response?.data?.message || 'حدث خطأ أثناء رفع الفيديو',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الفيديو بشكل نهائي؟')) {
      try {
        await axiosInstance.delete(`/feedback-videos/${id}`);
        toast({
          title: 'عملية ناجحة',
          description: 'تم حذف الفيديو بنجاح',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchVideos();
      } catch (error) {
        toast({
          title: 'خطأ',
          description: 'حدث خطأ أثناء حذف الفيديو',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'parent': return 'ولي أمر';
      case 'student': return 'تلميذ';
      case 'intro': return 'فيديو تقديمي';
      default: return type;
    }
  };

  const getTypeColor = (type) => {
    switch(type) {
      case 'parent': return 'cyan';
      case 'student': return 'yellow';
      case 'intro': return 'purple';
      default: return 'gray';
    }
  };

  const totalVideos = videos.length;
  const parentVideos = videos.filter(v => v.type === 'parent').length;
  const studentVideos = videos.filter(v => v.type === 'student').length;
  const introVideos = videos.filter(v => v.type === 'intro').length;

  return (
    <Box dir="rtl" minH="100vh" bg={bgColor} fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <SidebarAdmin />
        <Box flex="1" mr={contentMarginRight} p={{ base: 4, md: 8 }} transition="margin-right 0.3s ease" overflowY="auto">
          
          <Container maxW="container.xl" py={2}>
            <motion.div initial="hidden" animate="visible" variants={staggerContainer}>
              
              {/* Header Section */}
              <motion.div variants={fadeIn}>
                <Flex justify="space-between" align="center" mb={8} bg="white" p={6} borderRadius="2xl" shadow="sm" border="1px solid" borderColor="gray.100">
                  <Box>
                    <Heading size="lg" color="gray.800" mb={2}>إدارة فيديوهات آراء الزوار</Heading>
                    <Text color="gray.500" fontSize="md">قم برفع وتعديل وحذف الفيديوهات التي يتم عرضها في الصفحة الرئيسية للمنصة</Text>
                  </Box>
                  <Button
                    leftIcon={<FiPlus />}
                    colorScheme="yellow"
                    size="lg"
                    px={8}
                    shadow="md"
                    _hover={{ transform: 'translateY(-2px)', shadow: 'lg' }}
                    onClick={onOpen}
                  >
                    إضافة فيديو جديد
                  </Button>
                </Flex>
              </motion.div>

              {/* Statistics Section */}
              <motion.div variants={fadeIn}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
                  <StatCard title="إجمالي الفيديوهات" stat={totalVideos} icon={FiVideo} color="blue" />
                  <StatCard title="آراء الأولياء" stat={parentVideos} icon={FiUsers} color="cyan" />
                  <StatCard title="آراء التلاميذ" stat={studentVideos} icon={FiStar} color="yellow" />
                  <StatCard title="فيديوهات تقديمية" stat={introVideos} icon={FiMonitor} color="purple" />
                </SimpleGrid>
              </motion.div>

              {/* Main Table Card */}
              <motion.div variants={fadeIn}>
                <Card bg="white" shadow="sm" borderRadius="2xl" overflow="hidden" border="1px solid" borderColor="gray.100">
                  <CardHeader bg="white" borderBottom="1px solid" borderColor="gray.100" p={6}>
                    <Heading size="md" color="gray.700">قائمة الفيديوهات المرفوعة</Heading>
                  </CardHeader>
                  
                  <CardBody p={0}>
                    {loading ? (
                      <Center p={16}>
                        <VStack spacing={4}>
                          <CobraSpinner size="xl" color="yellow.500" thickness="4px" />
                          <Text color="gray.500" fontWeight="medium">جاري تحميل الفيديوهات...</Text>
                        </VStack>
                      </Center>
                    ) : videos.length === 0 ? (
                      <Center p={20}>
                        <VStack spacing={5}>
                          <Flex p={6} bg="gray.50" borderRadius="full" align="center" justify="center">
                            <Icon as={FiVideo} boxSize={12} color="gray.400" />
                          </Flex>
                          <Heading size="md" color="gray.600">لا توجد فيديوهات مسجلة</Heading>
                          <Text color="gray.500" textAlign="center" maxW="md">
                            لم يتم رفع أي فيديوهات حتى الآن. يمكنك البدء بإضافة فيديو جديد لعرضه على زوار الموقع.
                          </Text>
                          <Button mt={2} colorScheme="yellow" variant="outline" size="lg" leftIcon={<FiPlus />} onClick={onOpen}>
                            رفع أول فيديو
                          </Button>
                        </VStack>
                      </Center>
                    ) : (
                      <Box overflowX="auto">
                        <Table variant="simple" size="md">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th py={5} px={6} textAlign="right" color="gray.500" fontSize="sm" fontWeight="bold">تفاصيل الفيديو</Th>
                              <Th py={5} px={6} textAlign="right" color="gray.500" fontSize="sm" fontWeight="bold">الفئة</Th>
                              <Th py={5} px={6} textAlign="right" color="gray.500" fontSize="sm" fontWeight="bold">تاريخ الإضافة</Th>
                              <Th py={5} px={6} textAlign="center" color="gray.500" fontSize="sm" fontWeight="bold">إجراءات</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {videos.map((video) => (
                              <Tr key={video._id} _hover={{ bg: "gray.50" }} transition="background 0.2s">
                                <Td py={4} px={6}>
                                  <HStack spacing={4}>
                                    <Flex bg="blue.50" p={3} borderRadius="lg" color="blue.500">
                                      <Icon as={FiPlayCircle} boxSize={6} />
                                    </Flex>
                                    <Text fontWeight="700" color="gray.700" fontSize="md">{video.title}</Text>
                                  </HStack>
                                </Td>
                                <Td py={4} px={6}>
                                  <Badge colorScheme={getTypeColor(video.type)} px={4} py={1.5} borderRadius="full" fontSize="xs" fontWeight="bold">
                                    {getTypeLabel(video.type)}
                                  </Badge>
                                </Td>
                                <Td py={4} px={6}>
                                  <Text color="gray.600" fontSize="sm" fontWeight="medium">
                                    {new Date(video.createdAt).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' })}
                                  </Text>
                                </Td>
                                <Td py={4} px={6} textAlign="center">
                                  <Tooltip label="حذف الفيديو نهائياً" hasArrow placement="top">
                                    <IconButton
                                      icon={<FiTrash2 />}
                                      colorScheme="red"
                                      variant="ghost"
                                      aria-label="حذف"
                                      size="md"
                                      borderRadius="lg"
                                      _hover={{ bg: 'red.50', color: 'red.600' }}
                                      onClick={() => handleDelete(video._id)}
                                    />
                                  </Tooltip>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                  </CardBody>
                </Card>
              </motion.div>

            </motion.div>
          </Container>

          {/* Upload Modal */}
          <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent dir="rtl" borderRadius="2xl" p={2}>
              <ModalHeader color="gray.800" fontSize="2xl">إضافة فيديو جديد</ModalHeader>
              <ModalCloseButton left={6} right="auto" top={6} />
              
              <ModalBody mt={2}>
                <VStack spacing={6}>
                  <FormControl isRequired>
                    <FormLabel fontWeight="600" color="gray.700">عنوان الفيديو</FormLabel>
                    <Input
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="مثال: رأي ولي التلميذ أحمد بعد الدورة"
                      size="lg"
                      borderRadius="lg"
                      focusBorderColor="yellow.400"
                    />
                  </FormControl>
                  
                  <FormControl isRequired>
                    <FormLabel fontWeight="600" color="gray.700">الفئة (النوع)</FormLabel>
                    <Select 
                      name="type" 
                      value={formData.type} 
                      onChange={handleInputChange} 
                      size="lg" 
                      borderRadius="lg"
                      focusBorderColor="yellow.400"
                    >
                      <option value="parent">ولي أمر</option>
                      <option value="student">تلميذ</option>
                      <option value="intro">فيديو تقديمي</option>
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel fontWeight="600" color="gray.700">ملف الفيديو</FormLabel>
                    <Box
                      position="relative"
                      w="100%"
                      border="2px dashed"
                      borderColor={formData.file ? "yellow.400" : "gray.300"}
                      bg={formData.file ? "yellow.50" : "gray.50"}
                      borderRadius="xl"
                      p={8}
                      textAlign="center"
                      transition="all 0.2s"
                      _hover={{ borderColor: 'yellow.400', bg: 'yellow.50' }}
                    >
                      <Input
                        type="file"
                        accept="video/*"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        position="absolute"
                        top={0}
                        left={0}
                        opacity={0}
                        w="100%"
                        h="100%"
                        cursor="pointer"
                      />
                      <VStack spacing={3}>
                        <Flex p={4} bg="white" borderRadius="full" shadow="sm">
                          <Icon as={FiUpload} boxSize={8} color={formData.file ? "yellow.500" : "gray.400"} />
                        </Flex>
                        <Box>
                          <Text color={formData.file ? "yellow.700" : "gray.600"} fontWeight="bold" fontSize="lg">
                            {formData.file ? formData.file.name : 'اختر ملف فيديو'}
                          </Text>
                          {!formData.file && <Text color="gray.500" fontSize="sm" mt={1}>أو قم بسحب وإفلات الملف هنا</Text>}
                        </Box>
                      </VStack>
                    </Box>
                  </FormControl>
                </VStack>
              </ModalBody>

              <ModalFooter mt={4}>
                <Button variant="ghost" size="lg" mr={3} onClick={onClose} ml={3} borderRadius="lg">
                  إلغاء
                </Button>
                <Button
                  colorScheme="yellow"
                  size="lg"
                  px={8}
                  borderRadius="lg"
                  shadow="md"
                  onClick={handleSubmit}
                  isLoading={uploading}
                  loadingText="جاري الرفع..."
                >
                  رفع وحفظ
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

        </Box>
      </Flex>
    </Box>
  );
};

export default AdminFeedbackVideos;
